import { useState } from "react";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type { MeResponse, OrgResponse, TeamResponse, UserSummary } from "@/lib/types";

import { useAuthState } from "../../src/lib/auth";
import { ApiKeysTab } from "./api-keys-tab";
import { OrgTab } from "./org-tab";
import { ProfileTab } from "./profile-tab";
import { RetentionTab } from "./retention-tab";
import { SystemTab } from "./system-tab";
import { TeamTab } from "./team-tab";
import { UsageTab } from "./usage-tab";

interface SettingsTabsProps {
  initial: { team: TeamResponse; org: OrgResponse };
}

const TABS = [
  { value: "profile", label: "Profile" },
  { value: "team", label: "Team" },
  { value: "org", label: "Organization" },
  { value: "api-keys", label: "API Keys" },
  { value: "system", label: "System" },
  { value: "usage", label: "Usage" },
  { value: "retention", label: "Retention" },
] as const;

/**
 * Client tab container for the Settings page. Identity comes from the
 * dashboard-shell auth context (no extra ``/v1/me`` round-trip per route
 * change). Local team/org copies live here so child tabs can optimistically
 * mutate them after a successful PATCH without re-running the loader.
 *
 * After a profile PATCH the ProfileTab triggers
 * ``revalidator.revalidate()`` so the dashboard shell re-fetches ``/v1/me``
 * and the new name flows through the auth context (and the header).
 */
export function SettingsTabs({ initial }: SettingsTabsProps) {
  const auth = useAuthState();
  const [team, setTeam] = useState<TeamResponse>(initial.team);
  const [org, setOrg] = useState<OrgResponse>(initial.org);
  const isAdmin = auth.tenant.role === "team_admin";

  // Synthesize a MeResponse-shaped value for tabs that still type their
  // props that way. ``created_at`` is unused in the UI; the empty string
  // keeps the structural type happy.
  const me: MeResponse = {
    user: {
      id: auth.user.id,
      email: auth.user.email,
      name: auth.user.name,
      created_at: "",
    },
    tenant: { ...auth.tenant },
  };
  const meUser: UserSummary = me.user;

  return (
    <Tabs defaultValue="profile" className="w-full">
      <TabsList className="flex h-auto flex-wrap justify-start">
        {TABS.map((tab) => (
          <TabsTrigger key={tab.value} value={tab.value}>
            {tab.label}
          </TabsTrigger>
        ))}
      </TabsList>

      <TabsContent value="profile">
        <ProfileTab me={me} />
      </TabsContent>
      <TabsContent value="team">
        <TeamTab team={team} isAdmin={isAdmin} me={meUser} onUpdated={setTeam} />
      </TabsContent>
      <TabsContent value="org">
        <OrgTab org={org} isAdmin={isAdmin} onUpdated={setOrg} />
      </TabsContent>
      <TabsContent value="api-keys">
        <ApiKeysTab currentUserId={me.user.id} isAdmin={isAdmin} />
      </TabsContent>
      <TabsContent value="system">
        <SystemTab isAdmin={isAdmin} />
      </TabsContent>
      <TabsContent value="usage">
        <UsageTab />
      </TabsContent>
      <TabsContent value="retention">
        <RetentionTab />
      </TabsContent>
    </Tabs>
  );
}
